import type {
  BusinessAuditRecordDetail,
  BusinessAuditTimelineReadModel,
  BusinessAuditTimelineRecord,
} from "@billiard/contracts";
import { Download, RefreshCw, Search } from "lucide-react";
import { useId, useMemo, useState } from "react";
import "./BusinessAuditTimelinePanel.css";

function auditOccurredAtLabel(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

function auditMoneyLabel(valueFen: number): string {
  const yuan = Math.floor(valueFen / 100);
  const cents = String(valueFen % 100).padStart(2, "0");
  return `¥${yuan}.${cents}`;
}

function auditAdjustmentLabel(valueFen: number): string {
  if (valueFen === 0) return "不变 ¥0.00";
  return `${valueFen > 0 ? "增加" : "减少"} ${auditMoneyLabel(Math.abs(valueFen))}`;
}

function purchaseOrderStatusLabel(
  status: Extract<
    BusinessAuditRecordDetail,
    { kind: "PURCHASE_RECEIPT" }
  >["purchaseOrderStatusAfter"],
): string {
  return status === "RECEIVED" ? "采购单已全部收货" : "本次后仍有商品待收";
}

function cloudProjectionStatusLabel(
  status: Extract<BusinessAuditRecordDetail, { kind: "PURCHASE_RECEIPT" }>["cloudProjectionStatus"],
): string {
  if (status === "SYNCED") return "门店收货与云端记录已同步";
  if (status === "PENDING") return "门店已完成收货，正在同步到云端";
  return "门店收货已完成，对应云端记录待复核；其他经营可继续";
}

function sessionVoidCloudProjectionStatusLabel(
  status: Extract<BusinessAuditRecordDetail, { kind: "SESSION_VOID" }>["cloudProjectionStatus"],
): string {
  if (status === "SYNCED") return "门店作废与云端记录已同步";
  if (status === "PENDING") return "门店已完成作废，正在同步到云端";
  return "门店作废已完成，对应云端记录待复核；其他经营可继续";
}

function sessionStartAdjustmentCloudProjectionStatusLabel(
  status: Extract<
    BusinessAuditRecordDetail,
    { kind: "SESSION_START_ADJUSTMENT" }
  >["cloudProjectionStatus"],
): string {
  if (status === "SYNCED") return "门店调时与云端记录已同步";
  if (status === "PENDING") return "门店已完成调时，正在同步到云端";
  return "门店调时已完成，对应云端记录待复核；其他经营可继续";
}

function BusinessAuditDetail({
  detail,
}: {
  detail: BusinessAuditRecordDetail;
}): React.JSX.Element | null {
  if (detail.kind === "SUMMARY_ONLY") return null;
  if (detail.kind === "SOURCE_UNTRUSTED") {
    return (
      <output className="system-maintenance-note">
        本次操作的详细影响暂时无法安全确认，请回原业务工作区按业务单号核对。
      </output>
    );
  }
  if (detail.kind === "SESSION_VOID") {
    return (
      <details>
        <summary>查看本次误开台作废结果</summary>
        <dl>
          <dt>台单号</dt>
          <dd>{detail.orderNumber}</dd>
          <dt>球台</dt>
          <dd>{detail.tableNumber} 号台</dd>
          <dt>营业日</dt>
          <dd>{detail.businessDate}</dd>
          <dt>作废原因</dt>
          <dd>{detail.reason}</dd>
          <dt>台单结果</dt>
          <dd>未结台单已改为误开作废</dd>
          <dt>金额结果</dt>
          <dd>
            作废前 {auditMoneyLabel(detail.previousAmountFen)}，作废后{" "}
            {auditMoneyLabel(detail.currentAmountFen)}；实收与退款均为 ¥0.00
          </dd>
          <dt>云端记录</dt>
          <dd>{sessionVoidCloudProjectionStatusLabel(detail.cloudProjectionStatus)}</dd>
          <dt>作废完成时间</dt>
          <dd>
            <time dateTime={detail.voidedAt}>{auditOccurredAtLabel(detail.voidedAt)}</time>
          </dd>
        </dl>
      </details>
    );
  }
  if (detail.kind === "SESSION_START_ADJUSTMENT") {
    return (
      <details>
        <summary>查看本次开台时间更正结果</summary>
        <dl>
          <dt>台单号</dt>
          <dd>{detail.orderNumber}</dd>
          <dt>球台</dt>
          <dd>{detail.tableNumber} 号台</dd>
          <dt>营业日</dt>
          <dd>{detail.businessDate}</dd>
          <dt>修改原因</dt>
          <dd>{detail.reason}</dd>
          <dt>开台时间</dt>
          <dd>
            从{" "}
            <time dateTime={detail.previousStartedAt}>
              {auditOccurredAtLabel(detail.previousStartedAt)}
            </time>{" "}
            调整为{" "}
            <time dateTime={detail.currentStartedAt}>
              {auditOccurredAtLabel(detail.currentStartedAt)}
            </time>
          </dd>
          <dt>台费结果</dt>
          <dd>
            台费从 {auditMoneyLabel(detail.previousTableFeeFen)} 调整为{" "}
            {auditMoneyLabel(detail.currentTableFeeFen)}（
            {auditAdjustmentLabel(detail.adjustmentFen)}）
          </dd>
          <dt>云端记录</dt>
          <dd>{sessionStartAdjustmentCloudProjectionStatusLabel(detail.cloudProjectionStatus)}</dd>
          <dt>更正完成时间</dt>
          <dd>
            <time dateTime={detail.adjustedAt}>{auditOccurredAtLabel(detail.adjustedAt)}</time>
          </dd>
        </dl>
      </details>
    );
  }
  return (
    <details>
      <summary>查看本次收货影响</summary>
      <dl>
        <dt>采购单号</dt>
        <dd>{detail.purchaseOrderNumber}</dd>
        <dt>收货单号</dt>
        <dd>{detail.receiptNumber}</dd>
        <dt>营业日</dt>
        <dd>{detail.businessDate}</dd>
        <dt>本次商品</dt>
        <dd>{detail.productLineCount} 行</dd>
        <dt>库存影响</dt>
        <dd>库存价值增加 {auditMoneyLabel(detail.inventoryValueIncreaseFen)}</dd>
        <dt>应付影响</dt>
        <dd>
          未结应付从 {auditMoneyLabel(detail.payableTotalFenBefore)} 增加到{" "}
          {auditMoneyLabel(detail.payableTotalFenAfter)}（本次增加{" "}
          {auditMoneyLabel(detail.payableIncreaseFen)}）
        </dd>
        <dt>采购单进度</dt>
        <dd>{purchaseOrderStatusLabel(detail.purchaseOrderStatusAfter)}</dd>
        <dt>云端记录</dt>
        <dd>{cloudProjectionStatusLabel(detail.cloudProjectionStatus)}</dd>
        <dt>收货完成时间</dt>
        <dd>
          <time dateTime={detail.receivedAt}>{auditOccurredAtLabel(detail.receivedAt)}</time>
        </dd>
      </dl>
    </details>
  );
}

function businessAuditSearchValues(record: BusinessAuditTimelineRecord): string[] {
  const commonValues = [
    record.recordNumber,
    record.actionLabel,
    record.objectLabel,
    record.actorDisplayName,
    record.authorizerDisplayName ?? "无需现场授权",
  ];
  if (record.detail.kind === "PURCHASE_RECEIPT") {
    return [
      ...commonValues,
      record.detail.purchaseOrderNumber,
      record.detail.receiptNumber,
      record.detail.businessDate,
    ];
  }
  if (record.detail.kind === "SESSION_VOID") {
    return [
      ...commonValues,
      record.detail.orderNumber,
      record.detail.businessDate,
      record.detail.tableNumber,
      record.detail.reason,
    ];
  }
  if (record.detail.kind === "SESSION_START_ADJUSTMENT") {
    return [
      ...commonValues,
      record.detail.orderNumber,
      record.detail.businessDate,
      record.detail.tableNumber,
      record.detail.reason,
    ];
  }
  return commonValues;
}

function normalizedAuditSearchText(value: string): string {
  return value.trim().normalize("NFKC").toLocaleLowerCase("zh-CN");
}

function filterBusinessAuditTimeline(
  timeline: BusinessAuditTimelineReadModel,
  searchText: string,
): BusinessAuditTimelineReadModel {
  const query = normalizedAuditSearchText(searchText);
  if (!query) return timeline;
  const records = timeline.records.filter((record) =>
    businessAuditSearchValues(record).some((value) =>
      normalizedAuditSearchText(value).includes(query),
    ),
  );
  return {
    ...timeline,
    totalRecords: records.length,
    records,
    truncated: false,
  };
}

export function BusinessAuditTimelinePanel({
  timeline,
  isLoading,
  isError,
  isExporting,
  exportError,
  onRefresh,
  onExport,
}: {
  timeline: BusinessAuditTimelineReadModel | undefined;
  isLoading: boolean;
  isError: boolean;
  isExporting: boolean;
  exportError: boolean;
  onRefresh: () => void;
  onExport: (timeline: BusinessAuditTimelineReadModel) => void;
}): React.JSX.Element {
  const titleId = useId();
  const searchId = useId();
  const [searchText, setSearchText] = useState("");
  const isSearching = normalizedAuditSearchText(searchText).length > 0;
  const visibleTimeline = useMemo(
    () => (timeline ? filterBusinessAuditTimeline(timeline, searchText) : undefined),
    [searchText, timeline],
  );
  return (
    <section className="system-section business-audit-section" aria-labelledby={titleId}>
      <div className="section-heading">
        <div>
          <h2 id={titleId}>经营操作记录</h2>
          <p>
            查看本店谁在什么时候完成了什么操作；部分高风险操作可以展开核对业务结果，不显示手机号、凭据或原始日志。姓名按当前账号资料显示，操作记录本身不可修改。
          </p>
        </div>
        <div className="button-row">
          <button type="button" className="button secondary" onClick={onRefresh}>
            <RefreshCw size={16} aria-hidden="true" /> 刷新记录
          </button>
          <button
            type="button"
            className="button secondary"
            disabled={
              isLoading || isError || (visibleTimeline?.records.length ?? 0) === 0 || isExporting
            }
            onClick={() => {
              if (visibleTimeline) onExport(visibleTimeline);
            }}
          >
            <Download size={16} aria-hidden="true" />
            {isExporting ? "正在导出…" : isSearching ? "导出当前筛选结果" : "导出中文记录"}
          </button>
        </div>
      </div>
      {isLoading ? (
        <div className="loading-state" aria-live="polite">
          正在读取本店经营操作记录…
        </div>
      ) : null}
      {isError ? (
        <div className="error-band" role="alert">
          经营操作记录暂时无法确认。页面没有显示旧数据，请刷新后重试。
        </div>
      ) : null}
      {exportError ? (
        <div className="error-band" role="alert">
          中文操作记录没有下载成功；页面记录未受影响，请稍后重试。
        </div>
      ) : null}
      {!isLoading && !isError && timeline && timeline.records.length > 0 ? (
        <div className="business-audit-filter">
          <label htmlFor={searchId}>按业务信息查找</label>
          <div className="search-field">
            <Search size={16} aria-hidden="true" />
            <input
              id={searchId}
              type="search"
              aria-label="查找经营操作记录"
              value={searchText}
              onChange={(event) => setSearchText(event.currentTarget.value)}
              placeholder="输入台单号、采购单号、收货单号、操作记录号、操作人或操作内容"
              autoComplete="off"
            />
          </div>
          <small>
            系统只在当前已读取的最近 {timeline.records.length}{" "}
            条记录中查找；导出内容与当前筛选结果一致。
          </small>
        </div>
      ) : null}
      {!isLoading && !isError && timeline?.records.length === 0 ? (
        <div className="empty-state">本店还没有可显示的经营操作记录。</div>
      ) : null}
      {!isLoading &&
      !isError &&
      timeline &&
      timeline.records.length > 0 &&
      visibleTimeline &&
      visibleTimeline.records.length === 0 ? (
        <div className="empty-state">
          <p>没有找到与“{searchText.trim()}”相符的记录。</p>
          <button type="button" className="button secondary" onClick={() => setSearchText("")}>
            清空筛选，查看最近记录
          </button>
        </div>
      ) : null}
      {!isLoading &&
      !isError &&
      timeline &&
      timeline.records.length > 0 &&
      visibleTimeline &&
      visibleTimeline.records.length > 0 ? (
        <>
          <p className="system-maintenance-note">
            {isSearching
              ? `当前筛选找到 ${visibleTimeline.records.length} 条记录。`
              : `当前显示最近 ${timeline.records.length} 条，共记录 ${timeline.totalRecords} 条。`}
            {!isSearching && timeline.truncated ? "中文导出最多包含最近 1000 条。" : ""}
          </p>
          <div className="table-scroll">
            <table className="component-table">
              <caption className="visually-hidden">本店经营操作记录</caption>
              <thead>
                <tr className="component-row component-head">
                  <th scope="col">时间</th>
                  <th scope="col">操作人（当前姓名）</th>
                  <th scope="col">操作内容</th>
                  <th scope="col">关联业务</th>
                  <th scope="col">现场授权人（当前姓名）</th>
                </tr>
              </thead>
              <tbody>
                {visibleTimeline.records.map((record) => (
                  <tr className="component-row" key={record.recordNumber}>
                    <td>
                      <time dateTime={record.occurredAt}>
                        {auditOccurredAtLabel(record.occurredAt)}
                      </time>
                      <small>{record.recordNumber}</small>
                    </td>
                    <td>{record.actorDisplayName}</td>
                    <td>{record.actionLabel}</td>
                    <td>
                      {record.objectLabel}
                      <BusinessAuditDetail detail={record.detail} />
                    </td>
                    <td>{record.authorizerDisplayName ?? "无需现场授权"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}
